import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useEnv } from "../env/EnvProvider";
import { getUserManager } from "./oidc";

// Module-level guard against React StrictMode's double-invocation of
// useEffect in dev. The OIDC code in the callback URL is single-use —
// Keycloak invalidates it after the first /token exchange, so a second
// signinRedirectCallback() call surfaces "Code not valid" even though
// the first call succeeded. Keying on the code (or full search string)
// makes the second mount a no-op without affecting real navigations
// to a fresh callback URL.
const handledCodes = new Set<string>();

export function Callback() {
  const env = useEnv();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const codeKey = params.get("code") ?? window.location.search;
    if (handledCodes.has(codeKey)) {
      // Second StrictMode pass — the first pass already exchanged this
      // code. Bail out so we don't re-POST and 400 on Keycloak.
      return;
    }
    handledCodes.add(codeKey);

    let cancelled = false;
    const manager = getUserManager(env);
    manager
      .signinRedirectCallback()
      .then(() => {
        if (cancelled) return;
        navigate("/", { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        console.error("[auth] signinRedirectCallback failed", err);
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
  }, [env, navigate]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md rounded border border-bg-card-elevated bg-bg-card p-4 text-sm text-text-light">
          <div className="mb-2 font-semibold">Sign-in failed</div>
          <div className="mb-3 text-text-muted">{error}</div>
          <button
            type="button"
            onClick={() => navigate("/", { replace: true })}
            className="rounded bg-bg-card-elevated px-3 py-1.5 text-text-light hover:opacity-90"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center p-6 text-sm text-text-muted">
      Signing in…
    </div>
  );
}
